/* The one error shape the whole API returns.
 *
 * `coding.md`: error responses at the API edge are a single documented shape,
 * the same for every endpoint, and internal detail never crosses it. Defining
 * it here rather than in `apps/api` is what lets the clients narrow on it
 * instead of each inventing their own guess at the payload.
 *
 * Callers branch on `code`. `message` is for humans, is never parsed, and is
 * English — the localised string is chosen by the client from the code, per
 * `i18n.md`, because the server does not know the reader's locale on an
 * unauthenticated request and must not guess.
 */
import { z } from 'zod';
/** Machine-readable codes. Stable: renaming one is a breaking API change. */
export const ERROR_CODES = [
    'validation_failed',
    'unauthenticated',
    'forbidden',
    'not_found',
    'conflict',
    'rate_limited',
    'entitlement_exceeded',
    /* The request is legal and was not performed, because it looks like a
     * mistake and the server wants it said again on purpose — STORY-025's
     * far-future due date. Distinct from `conflict`: both answer 409, but one
     * means "somebody else changed it" and the other means "did you mean this?",
     * and a client that cannot tell them apart shows the wrong thing for one of
     * them. */
    'confirmation_required',
    /* Two codes rather than one, because the client says different things.
     * An expired link belongs to somebody who did the right thing too slowly, and
     * telling them to check they copied the address correctly is useless advice.
     *
     * They exist because the alternative was the client sniffing the English
     * message for the word "expired" — which is exactly the parsing this whole
     * scheme forbids, and it was written that way once before being caught. */
    'token_expired',
    'token_invalid',
    /* A third party we depend on could not be reached, and the request is worth
     * retrying unchanged — STORY-032's payment provider.
     *
     * Distinct from `internal_error`, and the difference is what the client
     * says. `internal_error` means we are broken and retrying is unlikely to
     * help; this means somebody else is, nothing was written, and trying again
     * in a moment is the right advice. Collapsing them would leave a customer
     * whose card is fine believing the product is broken — and `architecture.md`
     * requires a downstream outage to degrade a feature rather than read as a
     * failure of the whole system. */
    'provider_unavailable',
    'internal_error',
];
export const fieldErrorSchema = z.object({
    /** Dotted path into the request body, e.g. `password` or `venue.timezone`. */
    path: z.string(),
    /** Which rule failed, not prose. The client maps it to a localised message. */
    rule: z.string(),
});
/**
 * Why an `entitlement_exceeded` was refused, in numbers — STORY-045.
 *
 * **The client cannot compute this and must not try.** It knows neither the
 * plan's ceilings nor the workspace's usage, and a client that guessed would
 * disagree with the server the moment a plan changed. So the refusal carries
 * the arithmetic, and the client's only job is to put it in a sentence.
 *
 * Localised from these fields rather than from `message`, per `i18n.md`: the
 * counts are ICU arguments, and "remove 4 more" needs a plural rule the server
 * has no locale to choose.
 */
export const entitlementRefusalSchema = z.object({
    /** Which ceiling was hit: `activeEvents`, `seats` or `contacts`. */
    /* Null when the refusal is a capability rather than a ceiling — there is no
     * meter to count in that case, and inventing one would be a lie the client
     * would render. */
    meter: z.string().nullable(),
    /** What the current plan allows. */
    limit: z.number().int().nonnegative(),
    /** What the workspace is using now. */
    used: z.number().int().nonnegative(),
    /**
     * How many must go before the workspace is back within its plan.
     *
     * **Zero when it is merely full**, which is a different sentence: at the
     * limit there is nothing to remove and the answer is to upgrade or free one
     * up. Above zero means the workspace is genuinely over — after a downgrade or
     * a lapsed subscription — and this is exactly how many.
     */
    overBy: z.number().int().nonnegative(),
    /** The plan the workspace is on now. */
    plan: z.string(),
    /** The cheapest plan that lifts this ceiling, or null when none does. */
    liftedBy: z.string().nullable(),
});
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.enum(ERROR_CODES),
        message: z.string(),
        /** Present only on `validation_failed`. */
        fields: z.array(fieldErrorSchema).optional(),
        /** Present only on `entitlement_exceeded`. */
        entitlement: entitlementRefusalSchema.optional(),
        /** Echoes the request correlation id, so a user can quote it in a report. */
        requestId: z.string(),
    }),
});
//# sourceMappingURL=errors.js.map